import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";

export type MetadataAction = {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
};

export function MetadataActionButton({ action, disabled, showLabel }: {
  action: MetadataAction;
  disabled?: boolean;
  showLabel?: boolean;
}) {
  const Icon = action.icon;
  return (
    <Button
      variant={showLabel ? "outline" : "ghost"}
      size={showLabel ? "sm" : "icon-sm"}
      disabled={disabled || action.disabled}
      className={action.destructive
        ? "text-muted-foreground hover:bg-red-50 hover:text-red-600"
        : "text-muted-foreground hover:text-foreground"}
      title={action.label}
      aria-label={action.label}
      onClick={action.onClick}
    >
      <Icon />
      {showLabel && action.label}
    </Button>
  );
}

export function MetadataActions({ actions, disabled }: {
  actions: MetadataAction[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const regularActions = actions.filter(action => !action.destructive);
  const destructiveActions = actions.filter(action => action.destructive);
  const orderedActions = [...regularActions, ...destructiveActions];
  if (actions.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon-sm" disabled={disabled}
          className="text-muted-foreground" title={t("metadata.actions")} aria-label={t("metadata.actions")}>
          <MoreHorizontal />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4}
          className="z-50 min-w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          {orderedActions.map((action, index) => {
            const Icon = action.icon;
            const startsDestructiveActions = action.destructive && index === regularActions.length && index > 0;
            return (
              <DropdownMenu.Group key={action.label}>
                {startsDestructiveActions && <DropdownMenu.Separator className="my-1 h-px bg-border" />}
                <DropdownMenu.Item disabled={disabled || action.disabled} onSelect={action.onClick}
                  className={`flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-xs outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 ${action.destructive ? "text-red-600 focus:bg-red-50" : "focus:bg-accent focus:text-accent-foreground"}`}>
                  <Icon className="size-4" />
                  {action.label}
                </DropdownMenu.Item>
              </DropdownMenu.Group>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
